import Vue from "vue";
import Vuex from "vuex";
import { order } from "./modules/order";
import { address } from "./modules/address";
import { storeModule } from "./modules/store";
import { app } from "./modules/app";
import { selectedStore } from "./modules/selectedStore";
import { tableOrder } from "./modules/tableOrder";

Vue.use(Vuex);

/**
 * Main Vuex Store
 * Organized into modules:
 * - store: Store information, locations, categories, products
 * - app: Application state, UI state, loading states
 * - order: Cart items, order configuration, calculations
 * - address: Address management
 * - selectedStore: Selected store location (sucursal) - single source of truth
 */
export const store = new Vuex.Store({
  modules: {
    store: storeModule,
    app,
    order,
    address,
    selectedStore,
    tableOrder
  },
  getters: {
    // Cross-module getters
    isSidebarVisible: (state) => {
      // Check both order and tableOrder modules
      const orderItems = state.order.cartItems.length > 0;
      const tableOrderItems = state.tableOrder?.cartItems?.length > 0;
      return state.app.isCartPinned && (orderItems || tableOrderItems);
    },
    // Legacy getters for backward compatibility (delegating to modules)
    getStore: (state) => state.store.store,
    getStores: (state) => state.store.stores,
    getPromotions: (state) => state.store.promotions,
    getAppliedPromotions: (state) => state.order.appliedPromotions,
    getDiscountTotal: (state) => state.order.discountTotal,
    getCurrencySymbol: (state, getters) =>
      getters["store/getCurrencySymbol"] || "$",
    /** Formats a price for display (Euro format when store currency is EUR). */
    formatPrice: (state, getters) => getters["store/formatPrice"],
    getStoreName: (state) => state.store.storeName,
    getStoreAlias: (state) => state.store.storeAlias,
    getStoreAddressFields: (state) => state.store.store?.address_fields,
    getItems: (state) => state.order.items,
    getSubtotal: (state) => state.order.subtotal,
    getTipAmount: (state) => state.order.tipAmount,
    getTotalTax: (state) => state.order.totalTax,
    showSidebar: (state) => state.app.showSidebar,
    dining_option: (state) => state.store.dining_option,
    getServeAt: (state) => state.order.serve_at,
    getDeliveryAddress: (state) => state.order.delivery_address,
    getDiningOptionName: (state) => state.store.diningOptionName,
    getDiningOptionObject: (state) => state.store.diningOptionObject,
    getDeliveryFee: (state) =>
      state.store.diningOptionObject?.delivery_fee || 0,
    availableDiningOptions: (state) => state.store.availableDiningOptions,
    availablePaymentMethods: (state) => state.store.availablePaymentMethods,
    isStoreTaxInclusive: (state) => state.store.isStoreTaxInclusive,
    baseUrl: (state) => state.store.baseUrl,
    configLoader: (state) => state.app.configLoader,
    storeCountry: (state) => state.store.store?.country,
    getTaxRound: (state) => state.store.tax_round,
    getIsStoreClosed: (state) => state.store.isStoreClosed,
    getStoreTimezone: (state) => state.store.timezone || "UTC",
    getFetchingData: (state) => state.app.fetchingData,
    isScheduleEnabled: (state) => {
      const option = state.store.diningOptionObject;
      return option && option.is_scheduling_enabled === true;
    },
    totalItems: (state, getters) => {
      // Delegate to order module getter
      return getters["order/totalItems"];
    },
    subtotal: (state, getters) => {
      // Delegate to order module getter
      return getters["order/subtotal"];
    },
    filteredMenu: (state, getters) => {
      // Delegate to store module getter
      return getters["store/filteredMenu"];
    },
    // Legacy state getters for mapState compatibility
    categories: (state) => state.store.categories,
    products: (state) => state.store.products,
    promotions: (state) => state.store.promotions,
    cartItems: (state) => state.order.cartItems,
    selectedItem: (state) => state.app.selectedItem,
    selectedLocation: (state) =>
      state.selectedStore.selectedLocation || state.app.selectedLocation,
    isLocationModalOpen: (state) => {
      return state.app.isLocationModalOpen;
    },
    isScheduleModalOpen: (state) => state.app.isScheduleModalOpen,
    isRestaurantClosedModalOpen: (state) =>
      state.app.isRestaurantClosedModalOpen,
    scheduleModalOptions: (state) => state.app.scheduleModalOptions,
    isCartOpen: (state) => state.app.isCartOpen,
    isCartPinned: (state) => state.app.isCartPinned,
    orderConfig: (state) =>
      state.order.orderConfig || { type: "pickup", timing: "asap" },
    searchQuery: (state) => state.app.searchQuery,
    items: (state) => state.order.items
  },
  actions: {
    // Legacy actions for backward compatibility (delegating to modules)
    setStore({ dispatch }, payload) {
      return dispatch("store/setStore", payload);
    },
    setStores({ dispatch }, payload) {
      return dispatch("store/setStores", payload);
    },
    // Legacy action for backward compatibility (delegates to module)
    // NOTE: New code should use store/setStoreName directly
    setStoreName({ dispatch }, payload) {
      return dispatch("store/setStoreName", payload);
    },
    setStoreAlias({ dispatch }, payload) {
      return dispatch("store/setStoreAlias", payload);
    },
    setItems({ dispatch }, payload) {
      return dispatch("order/setItems", payload);
    },
    addItem({ dispatch }, payload) {
      return dispatch("order/addItem", payload);
    },
    updateItem({ dispatch }, payload) {
      return dispatch("order/updateItem", payload);
    },
    removeItem({ dispatch }, payload) {
      return dispatch("order/removeItem", payload);
    },
    setTaxRound({ dispatch }, payload) {
      return dispatch("store/setTaxRound", payload);
    },
    isStoreTaxInclusive({ dispatch }, payload) {
      return dispatch("store/isStoreTaxInclusive", payload);
    },
    setBaseUrl({ dispatch }, baseUrl) {
      return dispatch("store/setBaseUrl", baseUrl);
    },
    toggleConfigLoader({ dispatch }) {
      return dispatch("app/toggleConfigLoader");
    },
    setStoreTimezone({ dispatch }, payload) {
      return dispatch("store/setStoreTimezone", payload);
    },
    setCategories({ dispatch }, payload) {
      return dispatch("store/setCategories", payload);
    },
    setProducts({ dispatch }, payload) {
      return dispatch("store/setProducts", payload);
    },
    setPromotions({ dispatch }, payload) {
      return dispatch("store/setPromotions", payload);
    },
    addToCart({ dispatch }, payload) {
      return dispatch("order/addToCart", payload);
    },
    updateQuantity({ dispatch }, payload) {
      return dispatch("order/updateQuantity", payload);
    },
    removeItemCart({ dispatch }, cartId) {
      return dispatch("order/removeItemCart", cartId);
    },
    updateCartItem({ dispatch }, payload) {
      return dispatch("order/updateCartItem", payload);
    },
    setSelectedItem({ dispatch }, item) {
      return dispatch("app/setSelectedItem", item);
    },
    setSelectedLocation({ dispatch }, location) {
      return dispatch("app/setSelectedLocation", location);
    },
    setLocationModalOpen({ dispatch }, isOpen) {
      return dispatch("app/setLocationModalOpen", isOpen);
    },
    setScheduleModalOpen({ dispatch }, { isOpen, options = {} }) {
      return dispatch("app/setScheduleModalOpen", { isOpen, options });
    },
    setRestaurantClosedModalOpen({ dispatch }, isOpen) {
      return dispatch("app/setRestaurantClosedModalOpen", isOpen);
    },
    setDefaultScheduleIfNeeded({ dispatch }) {
      return dispatch("order/setDefaultScheduleIfNeeded", null, { root: true });
    },
    setCartOpen({ dispatch }, isOpen) {
      return dispatch("app/setCartOpen", isOpen);
    },
    setCartPinned({ dispatch }, isPinned) {
      return dispatch("app/setCartPinned", isPinned);
    },
    setOrderConfig({ dispatch }, config) {
      return dispatch("order/setOrderConfig", config);
    },
    setSearchQuery({ dispatch }, query) {
      return dispatch("app/setSearchQuery", query);
    },
    setSelectedCategory({ dispatch }, categoryId) {
      return dispatch("app/setSelectedCategory", categoryId);
    },
    setTipAmount({ dispatch }, payload) {
      return dispatch("order/setTipAmount", payload);
    },
    setServeAt({ dispatch }, payload) {
      return dispatch("order/setServeAt", payload);
    },
    setDeliveryAddress({ dispatch }, payload) {
      return dispatch("order/setDeliveryAddress", payload);
    },
    setAvailableDiningOptions({ commit }, payload) {
      return commit("store/setAvailableDiningOptions", payload);
    },
    setAvailablePaymentMethods({ commit }, payload) {
      return commit("store/setAvailablePaymentMethods", payload);
    },
    setDiningOption({ commit }, payload) {
      return commit("store/setDiningOption", payload);
    },
    setIsStoreClosed({ commit }, payload) {
      return commit("store/setIsStoreClosed", payload);
    }
  },
  mutations: {
    /**
     * Legacy mutations for backward compatibility
     * These mutations are kept for components that use mapMutations or direct commit()
     * New code should use module namespaced mutations instead
     */
    SET_ITEMS(state, payload) {
      state.order.items = payload;
    },
    SET_CATEGORIES(state, payload) {
      state.store.categories = payload;
    },
    SET_PRODUCTS(state, payload) {
      state.store.products = payload;
    },
    RECALCULATE(state) {
      // Delegate to order module and pass the current root state snapshot
      this.commit("order/RECALCULATE", { rootState: state }, { root: true });
    },
    setTipAmount(state, payload) {
      state.order.tipAmount = payload;
    },
    setAvailableDiningOptions(state, payload) {
      state.store.availableDiningOptions = payload;
    },
    setAvailablePaymentMethods(state, payload) {
      state.store.availablePaymentMethods = payload;
    },
    setDiningOption(state, payload) {
      this.commit("store/setDiningOption", payload);
    },
    setIsStoreClosed(state, payload) {
      state.store.isStoreClosed = payload;
    },
    // Legacy UI mutations (used by composables and components via direct commit)
    SET_SELECTED_ITEM(state, item) {
      state.app.selectedItem = item;
    },
    SET_SELECTED_LOCATION(state, location) {
      state.app.selectedLocation = location;
    },
    SET_LOCATION_MODAL_OPEN(state, isOpen) {
      state.app.isLocationModalOpen = isOpen;
    },
    SET_CART_OPEN(state, isOpen) {
      state.app.isCartOpen = isOpen;
    },
    SET_CART_PINNED(state, isPinned) {
      state.app.isCartPinned = isPinned;
    },
    SET_ORDER_CONFIG(state, config) {
      state.order.orderConfig = { ...state.order.orderConfig, ...config };
    },
    SET_SEARCH_QUERY(state, query) {
      state.app.searchQuery = query;
    },
    SET_SELECTED_CATEGORY(state, categoryId) {
      state.app.selectedCategoryId = categoryId;
    }
  }
});
